<template>
  <section
    id="demo-sites"
    v-motion
    class="max-w-[1200px] w-full px-6 py-20"
    :initial="sectionMotion.initial"
    :enter="sectionMotion.enter"
  >
    <div class="flex flex-col gap-4 text-center items-center mb-10">
      <h2 class="text-primary text-sm font-bold uppercase tracking-[0.2em]">{{ $t('landing.features.eyebrow') }}</h2>
      <h3 class="text-slate-900 dark:text-slate-100 text-3xl md:text-5xl font-black leading-tight max-w-[820px]">
        {{ $t('landing.features.title') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 text-base md:text-lg max-w-[760px]">
        {{ $t('landing.features.subtitle') }}
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
      <article
        v-for="(site, index) in demoSites"
        :key="site.url"
        v-motion
        :initial="cardMotion.initial"
        :enter="cardMotion.enter(index)"
        class="flex flex-col justify-between rounded-2xl border border-primary/15 bg-white dark:bg-background-dark/60 p-5 shadow-sm"
      >
        <div class="w-full aspect-[4/3] rounded-xl bg-slate-100 dark:bg-zinc-800 mb-4 overflow-hidden border border-slate-200/80 dark:border-zinc-700">
          <img
            v-if="site.previewUrl && !previewLoadFailed[site.previewId]"
            :src="site.previewUrl"
            :alt="site.businessTypeLabel"
            class="w-full h-full object-cover object-top"
            loading="lazy"
            @error="onPreviewError(site.previewId)"
          >
          <div v-else class="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500">
            <span class="material-symbols-outlined text-4xl" aria-hidden="true">dashboard</span>
          </div>
        </div>
        <p class="text-xs font-semibold tracking-wide uppercase text-primary/90 mb-2">
          {{ site.businessTypeLabel }}
        </p>
        <p class="text-base font-bold text-slate-900 dark:text-slate-100 break-all mb-4">
          {{ site.url }}
        </p>
        <a
          :href="site.url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex w-full items-center justify-center rounded-xl h-11 px-4 bg-primary text-white text-sm font-bold transition-all hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 cursor-pointer"
        >
          {{ $t('landing.cta.bookDemo', { businessType: site.businessTypeLabel }) }}
        </a>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReducedMotion } from '@/composables/useReducedMotion'

const { t } = useI18n()
const reducedMotion = useReducedMotion()
const previewLoadFailed = ref({})

const publicDomain = (import.meta.env.VITE_APP_PUBLIC_DOMAIN || '').toString().trim().replace(/^\.+/, '')
const isLocalDomain = publicDomain === 'localhost' || publicDomain.endsWith('.local')
const scheme = isLocalDomain ? 'http' : 'https'
function buildDefaultDemoUrl(slug) {
  if (!publicDomain) return '#'
  return `${scheme}://${slug}.${publicDomain}`
}

const demoSites = computed(() => ([
  {
    previewId: 'demo-restaurant',
    previewUrl: '/template-previews/demo-restaurant.png',
    businessTypeLabel: t('app.businessTypeRestaurants'),
    url: (import.meta.env.VITE_DEMO_SITE_RESTAURANT_URL || buildDefaultDemoUrl('demo-restaurant')).toString(),
  },
  {
    previewId: 'demo-service',
    previewUrl: '/template-previews/demo-service.png',
    businessTypeLabel: t('app.businessTypeServices'),
    url: (import.meta.env.VITE_DEMO_SITE_SERVICES_URL || buildDefaultDemoUrl('demo-service')).toString(),
  },
  {
    previewId: 'demo-ecommerce',
    previewUrl: '/template-previews/demo-ecommerce.png',
    businessTypeLabel: t('app.businessTypeEcommerce'),
    url: (import.meta.env.VITE_DEMO_SITE_ECOMMERCE_URL || buildDefaultDemoUrl('demo-ecommerce')).toString(),
  },
]))

function onPreviewError(previewId) {
  previewLoadFailed.value = { ...previewLoadFailed.value, [previewId]: true }
}

const sectionMotion = computed(() => {
  if (reducedMotion.value) {
    return { initial: { opacity: 1, y: 0 }, enter: { opacity: 1, y: 0, transition: { duration: 0 } } }
  }
  return {
    initial: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut', delay: 80 } },
  }
})

const cardMotion = computed(() => {
  if (reducedMotion.value) {
    return {
      initial: { opacity: 1, y: 0 },
      enter: () => ({ opacity: 1, y: 0, transition: { duration: 0 } }),
    }
  }
  return {
    initial: { opacity: 0, y: 16 },
    enter: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 120 + i * 70 },
    }),
  }
})
</script>
